import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const CUR_ICONS = { PLN: '🇵🇱', EUR: '🇪🇺', USD: '🇺🇸', USDT: '₮', GBP: '🇬🇧' };
const CUR_SYMBOLS = { PLN: 'zł', EUR: '€', USD: '$', USDT: '₮', GBP: '£' };
function fmt(n) { return (n || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(d) { return new Date(d).toLocaleDateString('pl-PL', { day: '2-digit', month: 'short' }); }
function fmtDateTime(d) { return new Date(d).toLocaleString('pl-PL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }

export default function MyFinance() {
  const { setPage, user, currentCompany } = useApp();
  const { t } = useI18n();
  const [balances, setBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [activeCurrency, setActiveCurrency] = useState('PLN');
  const [loading, setLoading] = useState(true);
  const [txFilter, setTxFilter] = useState('all'); // all, income, expense
  const [showAllTx, setShowAllTx] = useState(false);
  const [companyTotals, setCompanyTotals] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const isOwner = user?.account_type === 'own_company' || user?.role === 'company_owner' || user?.role === 'superadmin' || user?._original_role === 'superadmin';
      
      if (currentCompany?.id && isOwner) {
        // Company owner mode: load from accounting_entries
        const res = await api.get(`/accounting/company-finance?company_id=${currentCompany.id}`);
        const data = res.data;
        // Convert accounts to balance-like objects
        const accts = (data.accounts || []).map((a, i) => ({
          id: i + 1,
          currency: a.currency,
          amount: a.balance || 0,
        }));
        setBalances(accts.length > 0 ? accts : [{ id: 1, currency: 'PLN', amount: 0 }]);
        setTransactions(data.transactions || []);
        setCompanyTotals(data.totals || null);
      } else {
        // Incubator / personal mode: load personal balance
        const [bRes, tRes] = await Promise.all([
          api.get('/balance/my'),
          api.get('/balance/transactions?limit=50'),
        ]);
        const bData = bRes.data;
        setBalances(Array.isArray(bData) ? bData : (bData?.balances || []));
        const tData = tRes.data;
        setTransactions(Array.isArray(tData) ? tData : (tData?.transactions || tData?.items || []));
        setCompanyTotals(null);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [currentCompany?.id]);

  useEffect(() => { load(); }, [load]);

  const totalPLN = balances.reduce((s, b) => {
    if (b.currency === 'PLN') return s + (b.amount || 0);
    if (b.currency === 'EUR') return s + (b.amount || 0) * 4.25;
    if (b.currency === 'USD') return s + (b.amount || 0) * 3.95;
    return s + (b.amount || 0);
  }, 0);

  const activeBalance = balances.find(b => b.currency === activeCurrency);
  const filteredTx = transactions.filter(item => {
    if (txFilter === 'income') return item.amount > 0;
    if (txFilter === 'expense') return item.amount < 0;
    return true;
  });
  const visibleTx = showAllTx ? filteredTx : filteredTx.slice(0, 10);

  const incomeTotal = transactions.filter(item => item.amount > 0).reduce((s, item) => s + item.amount, 0);
  const expenseTotal = transactions.filter(item => item.amount < 0).reduce((s, item) => s + Math.abs(item.amount), 0);

  if (loading) return <div className="page"><BackButton /><div className="loader">{t('common.loading')}</div></div>;

  return (
    <div className="page" style={{ paddingBottom: 80 }}>
      <BackButton />

      {/* ===== HEADER: Total Balance ===== */}
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)',
        borderRadius: 20, padding: '28px 20px 22px', marginBottom: 16,
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative circles */}
        <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <div style={{ position: 'absolute', bottom: -20, left: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />

        <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 4 }}>{t('my_finance.total_balance')}</div>
        <div style={{ fontSize: 32, fontWeight: 'bold', letterSpacing: -1 }}>
          {fmt(totalPLN)} <span style={{ fontSize: 18, opacity: 0.7 }}>PLN</span>
        </div>

        {/* Mini stats */}
        <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.5 }}>↗ {t('my_finance.income_label')}</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#34d399' }}>+{fmt(incomeTotal)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, opacity: 0.5 }}>↙ {t('my_finance.expense_label')}</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#f87171' }}>-{fmt(expenseTotal)}</div>
          </div>
        </div>
      </div>

      {/* ===== ACCOUNT CARDS (horizontal scroll) ===== */}
      <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8, marginBottom: 16, WebkitOverflowScrolling: 'touch' }}>
        {balances.length === 0 ? (
          <div style={{
            background: 'linear-gradient(135deg, #374151, #4b5563)',
            borderRadius: 16, padding: '18px 20px', minWidth: 200, flex: '0 0 auto',
          }}>
            <div style={{ fontSize: 13, opacity: 0.7 }}>{t('my_finance.no_accounts')}</div>
            <div style={{ fontSize: 11, opacity: 0.5, marginTop: 4 }}>{t('my_finance.use_gateway')}</div>
          </div>
        ) : balances.map((b, i) => {
          const isActive = b.currency === activeCurrency;
          const gradients = {
            PLN: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
            EUR: 'linear-gradient(135deg, #1e3a2f, #059669)',
            USD: 'linear-gradient(135deg, #3a2e1e, #d97706)',
            USDT: 'linear-gradient(135deg, #1e3a30, #10b981)',
            GBP: 'linear-gradient(135deg, #2e1e3a, #7c3aed)',
          };
          return (
            <div key={i} onClick={() => setActiveCurrency(b.currency)} style={{
              background: gradients[b.currency] || gradients.PLN,
              borderRadius: 16, padding: '18px 20px', minWidth: 180, flex: '0 0 auto',
              cursor: 'pointer', position: 'relative', overflow: 'hidden',
              border: isActive ? '2px solid rgba(255,255,255,0.4)' : '2px solid transparent',
              transition: 'all 0.2s',
            }}>
              <div style={{ position: 'absolute', top: -10, right: -10, fontSize: 48, opacity: 0.1 }}>
                {CUR_ICONS[b.currency] || '💰'}
              </div>
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 2 }}>
                {CUR_ICONS[b.currency]} {b.currency}
              </div>
              <div style={{ fontSize: 24, fontWeight: 'bold' }}>
                {fmt(b.amount)} <span style={{ fontSize: 14, opacity: 0.7 }}>{CUR_SYMBOLS[b.currency]}</span>
              </div>
              <div style={{ fontSize: 11, opacity: 0.5, marginTop: 6 }}>
                •••• •••• {String(b.id || i).padStart(4, '0')}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== QUICK ACTIONS ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 20 }}>
        {[
          { icon: '📤', label: t('my_finance.transfer'), page: 'withdrawals' },
          { icon: '📥', label: t('my_finance.top_up'), page: 'top_up' },
          { icon: '💱', label: t('my_finance.exchange'), page: 'exchange' },
          { icon: '📄', label: t('my_finance.invoice'), page: 'invoices_out' },
        ].map((a, i) => (
          <div key={i} onClick={() => setPage(a.page)} style={{
            background: 'rgba(255,255,255,0.06)', borderRadius: 14, padding: '14px 8px',
            textAlign: 'center', cursor: 'pointer', transition: 'background 0.2s',
          }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>{a.icon}</div>
            <div style={{ fontSize: 11, opacity: 0.8 }}>{a.label}</div>
          </div>
        ))}
      </div>

      {/* ===== TRANSACTIONS ===== */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>{t('my_finance.transactions')}</h3>
          <div style={{ display: 'flex', gap: 4 }}>
            {['all', 'income', 'expense'].map(f => (
              <button key={f} onClick={() => setTxFilter(f)} style={{
                padding: '4px 10px', borderRadius: 8, border: 'none', fontSize: 11, cursor: 'pointer',
                background: txFilter === f ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.06)',
                color: '#fff',
              }}>
                {f === 'all' ? t('my_finance.filter_all') : f === 'income' ? `↗ ${t('my_finance.filter_income')}` : `↙ ${t('my_finance.filter_expense')}`}
              </button>
            ))}
          </div>
        </div>

        {visibleTx.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, opacity: 0.4 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📭</div>
            <div>{t('my_finance.no_transactions')}</div>
          </div>
        ) : (
          <div>
            {visibleTx.map((tx, i) => {
              const isIncome = tx.amount > 0;
              const prevDate = i > 0 ? fmtDate(visibleTx[i-1].created_at) : null;
              const curDate = fmtDate(tx.created_at);
              const showDateHeader = curDate !== prevDate;

              return (
                <div key={tx.id || i}>
                  {showDateHeader && (
                    <div style={{ fontSize: 12, opacity: 0.4, padding: '10px 0 4px', fontWeight: 600 }}>
                      {curDate}
                    </div>
                  )}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                  }}>
                    {/* Icon */}
                    <div style={{
                      width: 42, height: 42, borderRadius: 12,
                      background: isIncome ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                      flexShrink: 0,
                    }}>
                      {tx.tx_type === 'gateway' ? '🚀' :
                       tx.tx_type === 'withdrawal' ? '📤' :
                       tx.tx_type === 'deposit' ? '📥' :
                       tx.tx_type === 'exchange' ? '💱' :
                       tx.tx_type === 'payout' ? '💸' :
                       tx.tx_type === 'fee' ? '🏦' :
                       isIncome ? '↗' : '↙'}
                    </div>

                    {/* Details */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {tx.description || tx.tx_type || t('my_finance.transaction')}
                      </div>
                      <div style={{ fontSize: 11, opacity: 0.5 }}>
                        {fmtDateTime(tx.created_at)} · {tx.currency || 'PLN'}
                      </div>
                    </div>

                    {/* Amount */}
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{
                        fontWeight: 600, fontSize: 15,
                        color: isIncome ? '#34d399' : '#f87171',
                      }}>
                        {isIncome ? '+' : ''}{fmt(tx.amount)} {CUR_SYMBOLS[tx.currency] || 'zł'}
                      </div>
                      {tx.balance_after !== undefined && (
                        <div style={{ fontSize: 11, opacity: 0.4 }}>
                          = {fmt(tx.balance_after)} {CUR_SYMBOLS[tx.currency] || 'zł'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredTx.length > 10 && !showAllTx && (
              <button onClick={() => setShowAllTx(true)} style={{
                width: '100%', padding: 14, marginTop: 8, borderRadius: 12,
                background: 'rgba(255,255,255,0.06)', border: 'none', color: '#fff',
                fontSize: 13, cursor: 'pointer',
              }}>
                {t('my_finance.show_more')} ({filteredTx.length - 10})
              </button>
            )}
          </div>
        )}
      </div>

      {/* ===== BOTTOM QUICK LINKS ===== */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 16,
      }}>
        {[
          { icon: '💳', label: t('my_finance.payment_links'), page: 'payment_links' },
          { icon: '🧾', label: t('my_finance.receipts_vat'), page: 'receipts' },
          { icon: '📱', label: t('my_finance.qr_payment'), page: 'qr_payment' },
        ].map((a, i) => (
          <div key={i} onClick={() => setPage(a.page)} style={{
            background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: '12px 8px',
            textAlign: 'center', cursor: 'pointer',
          }}>
            <div style={{ fontSize: 20 }}>{a.icon}</div>
            <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4 }}>{a.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
